import type { BoardPost, ViewOptions } from './board';
import { DAY_MS } from './constants';
import { distanceKm, geohashCenter } from './geo';
import type { TileSummary } from './types';

/**
 * Paid posts ("promotions"): an author pays for their post to be whispered more, around their town
 * or worldwide, for a few days: a yellow whisper marked Sponsored that stays on screen longer and
 * comes round more often, whatever its opens. The voices in the middle stay what people open; a
 * sponsored post gets a voice only that way.
 */

export type PromotionPlanId = 'town-1d' | 'town-7d' | 'world-1d';

/** Currencies a promotion can be paid in. USD needs Razorpay's international payments. */
export type Currency = 'INR' | 'USD';
export const CURRENCIES: readonly Currency[] = ['INR', 'USD'];

export interface PromotionPlan {
  id: PromotionPlanId;
  /** 'town': readers within PROMOTION_TOWN_KM of the post; 'world': every reader. */
  reach: 'town' | 'world';
  days: number;
  /** Price per currency, in its smallest unit (paise, cents). */
  prices: Record<Currency, number>;
}

export const PROMOTION_PLANS: readonly PromotionPlan[] = [
  { id: 'town-1d', reach: 'town', days: 1, prices: { INR: 49_00, USD: 1_99 } },
  { id: 'town-7d', reach: 'town', days: 7, prices: { INR: 249_00, USD: 9_99 } },
  { id: 'world-1d', reach: 'world', days: 1, prices: { INR: 499_00, USD: 4_99 } },
];

/** How far around the post a town promotion reaches (km). */
export const PROMOTION_TOWN_KM = 100;

export function promotionPlan(id: unknown): PromotionPlan | null {
  return PROMOTION_PLANS.find((p) => p.id === id) ?? null;
}

export function isCurrency(v: unknown): v is Currency {
  return CURRENCIES.includes(v as Currency);
}

/** A plan in words, e.g. "Around your town for 7 days". */
export function describePlan(plan: PromotionPlan): string {
  const where = plan.reach === 'world' ? 'Worldwide' : 'Around your town';
  return `${where} for ${plan.days === 1 ? '1 day' : `${plan.days} days`}`;
}

/** An amount in a currency's smallest unit, e.g. (24900, INR) → "₹249", (199, USD) → "$1.99". */
export function formatPrice(amount: number, currency: Currency): string {
  const whole = amount % 100 === 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount / 100);
}

/**
 * Firestore `promotions/{orderId}` (the payment gateway's order id). Created when the author starts
 * paying; `review` once paid, waiting for an admin; `active` while shown; then `ended`, or
 * `refunded` when an admin turned it down (`refunding` while the gateway does it).
 */
export type PromotionStatus = 'created' | 'review' | 'active' | 'ended' | 'refunding' | 'refunded';

export interface PromotionDoc {
  postId: string;
  uid: string;
  plan: PromotionPlanId;
  /** In the currency's smallest unit. */
  amount: number;
  currency: Currency;
  status: PromotionStatus;
  /** Epoch ms. */
  createdAt: number;
  paymentId: string | null;
  paidAt: number | null;
  startsAt: number | null;
  endsAt: number | null;
}

/** A post being promoted, as every reader's board sees it (`sponsored/board`, public). */
export interface SponsoredTile extends TileSummary {
  promotionId: string;
  /** null: every reader; otherwise readers within this many km of the post. */
  reachKm: number | null;
  /** Epoch ms. */
  endsAt: number;
}

export interface SponsoredBoard {
  tiles: SponsoredTile[];
}

/** The days a plan runs for, from `start`. */
export const promotionEnd = (plan: PromotionPlan, start: number) => start + plan.days * DAY_MS;

/**
 * The promotions running now that reach a reader, as sponsored whispers. A town promotion reaches
 * readers with a location within its reach (whatever distance they picked); a worldwide one
 * reaches everyone.
 */
export function sponsoredInView(
  tiles: readonly SponsoredTile[],
  view: Pick<ViewOptions, 'center' | 'now'>,
): BoardPost[] {
  return tiles
    .filter((t) => t.endsAt > view.now)
    .map((t) => ({
      t,
      km: t.g5 && view.center ? distanceKm(view.center, geohashCenter(t.g5)) : null,
    }))
    .filter(({ t, km }) => t.reachKm === null || (km !== null && km <= t.reachKm))
    .sort((a, b) => a.t.promotionId.localeCompare(b.t.promotionId))
    .map(({ t, km }) => ({
      id: t.id,
      title: t.title,
      excerpt: t.excerpt,
      thumb: t.thumb,
      author: t.author,
      isAgent: t.isAgent,
      g5: t.g5,
      createdAt: t.createdAt,
      score: 0,
      distanceKm: km,
      sponsored: true,
    }));
}
